import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Layout from "../../Components/Layout/Layout";
import Loader from "../../Components/Loader/Loader";
import {
  getSchedules,
  runScheduleNow,
  deleteSchedule,
  updateSchedule,
} from "../../Components/AtlasChatComponent/reports/reportsApi";
import { FREQUENCIES } from "../../Components/AtlasChatComponent/reports/ScheduleStep";
import { minStartLocal } from "../../Components/AtlasChatComponent/reports/useReportsMode";
import { GetUserId } from "../../Utils/Utils";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Button, CircularProgress, Modal, Switch, Tooltip } from "@mui/material";
import "./ScheduledReports.css";

const FALLBACK_USER_ID = "2619061";

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// ISO string -> local "YYYY-MM-DDTHH:mm" for a datetime-local input.
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
};

// datetime-local "YYYY-MM-DDTHH:mm" -> "YYYY-MM-DDTHH:mm:ss" (no tz), matching
// the create-schedule contract.
const toApiDatetime = (local) => {
  if (!local) return local;
  return local.length === 16 ? `${local}:00` : local;
};

const ScheduleCard = ({
  schedule,
  onRunNow,
  onDelete,
  onEdit,
  onToggleActive,
  toggling,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isActive = schedule?.is_active;
  const toEmails = schedule?.payload?.notification?.to_email || [];

  return (
    <div className="sched-card">
      <div
        className="sched-card-head"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <div className="sched-card-title-wrap">
          <p className="sched-card-title">{schedule?.name || "Untitled report"}</p>
          <p className="sched-card-id">ID: {schedule?.id}</p>
        </div>

        <div className="sched-card-meta">
          {schedule?.frequency ? (
            <span className="sched-chip-frequency">
              {String(schedule.frequency).toUpperCase()}
            </span>
          ) : null}

          <div
            className="sched-card-actions"
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip title={isActive ? "Active" : "Inactive"}>
              <Switch
                size="small"
                checked={!!isActive}
                onChange={(e) => onToggleActive(schedule, e.target.checked)}
                disabled={toggling}
                className="sched-action-switch"
              />
            </Tooltip>
            <span
              className={`sched-chip-status ${
                isActive
                  ? "sched-chip-status--active"
                  : "sched-chip-status--inactive"
              }`}
            >
              {isActive ? "ACTIVE" : "INACTIVE"}
            </span>

            <Tooltip title="Run now">
              <button
                type="button"
                className="sched-action-btn"
                onClick={() => onRunNow(schedule)}
              >
                <PlayArrowRoundedIcon fontSize="small" />
              </button>
            </Tooltip>
            <Tooltip title="Edit">
              <button
                type="button"
                className="sched-action-btn sched-action-btn--edit"
                onClick={() => onEdit(schedule)}
              >
                <EditOutlinedIcon fontSize="small" />
              </button>
            </Tooltip>
            <Tooltip title="Delete">
              <button
                type="button"
                className="sched-action-btn sched-action-btn--delete"
                onClick={() => onDelete(schedule)}
              >
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </button>
            </Tooltip>
          </div>

          <ExpandMoreOutlinedIcon
            className={`sched-card-chevron ${
              expanded ? "sched-card-chevron--open" : ""
            }`}
          />
        </div>
      </div>

      {expanded ? (
        <div className="sched-card-body">
          <div className="sched-detail-grid">
            <div className="sched-detail">
              <span className="sched-detail-label">Next Scheduled Run</span>
              <span className="sched-detail-value">
                {formatDateTime(schedule?.next_run_at)}
              </span>
            </div>
            <div className="sched-detail">
              <span className="sched-detail-label">Last Run</span>
              <span className="sched-detail-value">
                {formatDateTime(schedule?.last_run_at)}
              </span>
            </div>
            <div className="sched-detail">
              <span className="sched-detail-label">Timezone</span>
              <span className="sched-detail-value">
                {schedule?.timezone || "—"}
              </span>
            </div>
            <div className="sched-detail">
              <span className="sched-detail-label">Created</span>
              <span className="sched-detail-value">
                {formatDateTime(schedule?.created_at)}
              </span>
            </div>
            <div className="sched-detail">
              <span className="sched-detail-label">Recipients</span>
              <span className="sched-detail-value">
                {toEmails.length ? toEmails.join(", ") : "—"}
              </span>
            </div>
            <div className="sched-detail">
              <span className="sched-detail-label">Job Type</span>
              <span className="sched-detail-value">
                {schedule?.job_type || "—"}
              </span>
            </div>
          </div>

          {schedule?.payload?.user_request ? (
            <div className="sched-detail sched-detail--full">
              <span className="sched-detail-label">Report Request</span>
              <p className="sched-request-text">
                {schedule.payload.user_request}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const EditScheduleModal = ({ schedule, onClose, onSave }) => {
  const seededStart = toLocalInput(schedule?.next_run_at);
  const [name, setName] = useState(schedule?.name || "");
  const [frequency, setFrequency] = useState(schedule?.frequency || "once");
  const [startAt, setStartAt] = useState(seededStart);
  const [isActive, setIsActive] = useState(!!schedule?.is_active);
  const [saving, setSaving] = useState(false);

  const min = minStartLocal();
  const seededStartTooSoon = !!seededStart && seededStart < min;
  const startTooSoon = !!startAt && startAt < min;
  const startChanged = startAt !== seededStart;
  const freqChanged = frequency !== (schedule?.frequency || "once");
  const needsValidStart = startChanged || (freqChanged && seededStartTooSoon);
  const startInvalid = needsValidStart && (!startAt || startTooSoon);

  const nameChanged = name.trim() !== (schedule?.name || "");
  const activeChanged = isActive !== !!schedule?.is_active;
  const hasChanges = nameChanged || freqChanged || activeChanged || startChanged;
  const canSave =
    !saving && hasChanges && name.trim().length > 0 && !startInvalid;

  const handleSave = async () => {
    const changes = {};
    if (nameChanged) changes.name = name.trim();
    if (freqChanged) changes.frequency = frequency;
    if (activeChanged) changes.is_active = isActive;
    if (startChanged) changes.start_at = toApiDatetime(startAt);
    if (Object.keys(changes).length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await onSave(schedule, changes);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sched-confirm-modal sched-edit-modal">
      <h2 className="sched-confirm-title">Edit schedule</h2>

      <div className="sched-edit-field">
        <label className="sched-edit-label" htmlFor="sched-edit-name">
          Name
        </label>
        <input
          id="sched-edit-name"
          type="text"
          className="sched-edit-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={saving}
        />
      </div>

      <div className="sched-edit-field">
        <span className="sched-edit-label">Frequency</span>
        <div className="sched-freq-grid">
          {FREQUENCIES.map((f) => (
            <button
              type="button"
              key={f.value}
              className={`sched-freq-option${
                frequency === f.value ? " sched-freq-option--selected" : ""
              }`}
              aria-pressed={frequency === f.value}
              onClick={() => setFrequency(f.value)}
              disabled={saving}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="sched-edit-field">
        <label className="sched-edit-label" htmlFor="sched-edit-start">
          Start time
        </label>
        <input
          id="sched-edit-start"
          type="datetime-local"
          className="sched-edit-input"
          min={min}
          value={startAt}
          onChange={(e) => setStartAt(e.target.value)}
          disabled={saving}
        />
        {startInvalid ? (
          <span className="sched-edit-error">
            Start time must be at least one hour from now.
          </span>
        ) : null}
      </div>

      <div className="sched-edit-field sched-edit-field--row">
        <label className="sched-edit-label sched-edit-label--inline">
          Active
        </label>
        <Switch
          size="small"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          disabled={saving}
          className="sched-action-switch"
        />
      </div>

      <div className="sched-confirm-actions">
        <Button
          className="sched-confirm-cancel"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          className="sched-confirm-run"
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
};

const ScheduledReports = () => {
  const [loading, setLoading] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [runTarget, setRunTarget] = useState(null);
  const [running, setRunning] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    const fetchSchedules = async () => {
      setLoading(true);
      try {
        const userId = GetUserId() || FALLBACK_USER_ID;
        const data = await getSchedules({ userId });
        setSchedules(Array.isArray(data) ? data : []);
      } catch (error) {
        toast.error("Failed to load scheduled reports");
        setSchedules([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSchedules();
  }, []);

  const handleConfirmRunNow = async () => {
    if (!runTarget) return;
    setRunning(true);
    try {
      await runScheduleNow({ scheduleId: runTarget.id });
      toast.success("Report run triggered");
      setRunTarget(null);
    } catch (error) {
      toast.error("Failed to trigger report run");
    } finally {
      setRunning(false);
    }
  };

  const handleToggleActive = async (schedule, nextValue) => {
    setTogglingId(schedule.id);
    // Optimistic update.
    setSchedules((prev) =>
      prev.map((s) =>
        s.id === schedule.id ? { ...s, is_active: nextValue } : s,
      ),
    );
    try {
      await updateSchedule({
        scheduleId: schedule.id,
        changes: { is_active: nextValue },
      });
    } catch (error) {
      // Revert on failure.
      setSchedules((prev) =>
        prev.map((s) =>
          s.id === schedule.id ? { ...s, is_active: !nextValue } : s,
        ),
      );
      toast.error("Failed to update schedule");
    } finally {
      setTogglingId(null);
    }
  };

  const handleSaveEdit = async (schedule, changes) => {
    try {
      const updated = await updateSchedule({
        scheduleId: schedule.id,
        changes,
      });
      setSchedules((prev) =>
        prev.map((s) =>
          s.id === schedule.id
            ? updated && updated.id
              ? updated
              : { ...s, ...changes }
            : s,
        ),
      );
      toast.success("Schedule updated");
      setEditTarget(null);
    } catch (error) {
      toast.error("Failed to update schedule");
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const userId = GetUserId() || FALLBACK_USER_ID;
      await deleteSchedule({ scheduleId: deleteTarget.id, userId });
      setSchedules((prev) => prev.filter((s) => s.id !== deleteTarget.id));
      toast.success("Schedule deleted");
      setDeleteTarget(null);
    } catch (error) {
      toast.error("Failed to delete schedule");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Layout>
      {loading ? <Loader /> : null}
      <div className="scheduled-reports-page">
        <h1 className="sched-page-headline">Scheduled Reports</h1>

        {!loading && schedules.length === 0 ? (
          <div className="sched-empty">No scheduled reports yet.</div>
        ) : (
          <div className="sched-card-list">
            {schedules.map((schedule) => (
              <ScheduleCard
                key={schedule?.id}
                schedule={schedule}
                onRunNow={setRunTarget}
                onDelete={setDeleteTarget}
                onEdit={setEditTarget}
                onToggleActive={handleToggleActive}
                toggling={togglingId === schedule?.id}
              />
            ))}
          </div>
        )}
      </div>

      <Modal
        open={!!runTarget}
        onClose={() => (running ? null : setRunTarget(null))}
      >
        <div className="sched-confirm-modal">
          <h2 className="sched-confirm-title">Run report now?</h2>
          <p className="sched-confirm-text">
            This will trigger an immediate run of
            {runTarget?.name ? (
              <span className="sched-confirm-name"> “{runTarget.name}”</span>
            ) : (
              " this report"
            )}
            . Recipients will receive the report when it completes.
          </p>
          <div className="sched-confirm-actions">
            <Button
              className="sched-confirm-cancel"
              onClick={() => setRunTarget(null)}
              disabled={running}
            >
              Cancel
            </Button>
            <Button
              className="sched-confirm-run"
              variant="contained"
              onClick={handleConfirmRunNow}
              disabled={running}
              startIcon={
                running ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PlayArrowRoundedIcon fontSize="small" />
                )
              }
            >
              {running ? "Triggering…" : "Run now"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!deleteTarget}
        onClose={() => (deleting ? null : setDeleteTarget(null))}
      >
        <div className="sched-confirm-modal">
          <h2 className="sched-confirm-title">Delete schedule?</h2>
          <p className="sched-confirm-text">
            This will permanently delete
            {deleteTarget?.name ? (
              <span className="sched-confirm-name"> “{deleteTarget.name}”</span>
            ) : (
              " this schedule"
            )}
            . This action cannot be undone.
          </p>
          <div className="sched-confirm-actions">
            <Button
              className="sched-confirm-cancel"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              className="sched-confirm-run"
              variant="contained"
              onClick={handleConfirmDelete}
              disabled={deleting}
              startIcon={
                deleting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                )
              }
            >
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={!!editTarget} onClose={() => setEditTarget(null)}>
        <div>
          {editTarget ? (
            <EditScheduleModal
              key={editTarget.id}
              schedule={editTarget}
              onClose={() => setEditTarget(null)}
              onSave={handleSaveEdit}
            />
          ) : null}
        </div>
      </Modal>
    </Layout>
  );
};

export default ScheduledReports;
